import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { BaseElement } from "../utils/ui-components";
import { chevronDownIcon, closeIcon } from "../utils/icons";
import { getSourcePath } from "../data/types";

@customElement("source-sidebar")
export class SourceSidebarElement extends BaseElement {
    @property({ type: Boolean })
    sidebarOpen = false;

    @property({ type: Array })
    sources: Array<{
        sourcePath: string;
        shortName?: string;
        displayName: string;
        paths: Array<{ path: string; count: number }>;
    }> = [];

    @property({ type: Number })
    totalDocuments = 0;

    @property({ type: Set })
    selectedPaths: Set<string> = new Set();

    @property()
    onCloseSidebar?: () => void;

    private togglePath(path: string) {
        const newPaths = new Set(this.selectedPaths);

        // Find the source this path belongs to
        const source = this.sources.find((s) => path.startsWith(s.sourcePath));
        if (!source) return;

        if (path === source.sourcePath) {
            // If clicking the source itself
            const sourcePaths = source.paths.map((p) => p.path);
            const hasAllPaths = sourcePaths.every((p) => this.selectedPaths.has(p) || this.selectedPaths.has(source.sourcePath));

            if (hasAllPaths) {
                // Deselect only paths from this source
                sourcePaths.forEach((p) => newPaths.delete(p));
                newPaths.delete(source.sourcePath);
            } else {
                // Select all paths from this source while preserving other selections
                sourcePaths.forEach((p) => newPaths.add(p));
            }
        } else {
            // Toggle individual path
            if (newPaths.has(path)) {
                newPaths.delete(path);
            } else {
                newPaths.add(path);
            }
        }

        // Update internal state and dispatch event with full paths
        this.selectedPaths = newPaths;
        this.dispatchEvent(
            new CustomEvent("pathsChanged", {
                detail: { paths: Array.from(newPaths) },
                bubbles: true,
                composed: true,
            })
        );
    }

    render() {
        return html`
            <div class="h-full flex flex-col bg-white">
                <div class="h-12 flex-shrink-0 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
                    <div class="flex items-center justify-between flex-1 lg:mr-0 mr-2">
                        <h2 class="text-base font-medium text-gray-900">Sources</h2>
                        ${this.selectedPaths.size > 0
                            ? html`
                                  <button
                                      @click=${() => {
                                          this.dispatchEvent(
                                              new CustomEvent("pathsChanged", {
                                                  detail: { paths: [] },
                                                  bubbles: true,
                                                  composed: true,
                                              })
                                          );
                                      }}
                                      class="text-sm text-blue-600 hover:text-blue-800"
                                  >
                                      Clear filters
                                  </button>
                              `
                            : ""}
                    </div>
                    <button @click=${this.onCloseSidebar} class="lg:hidden p-1 rounded-md hover:bg-gray-100">
                        <i class="w-4 h-4 icon">${closeIcon}</i>
                    </button>
                </div>
                <div class="flex-1 min-h-0 overflow-y-auto p-4">
                    <div class="space-y-4">
                        ${this.sources.map((source) => {
                            const allPathsSelected = source.paths.every(
                                (p) => this.selectedPaths.has(p.path) || this.selectedPaths.has(source.sourcePath)
                            );
                            const showChevron = !(source.paths.length == 1 && source.paths[0].path == source.sourcePath);

                            return html`
                                <details class="group mt-1">
                                    <summary
                                        class="w-full text-left px-2 py-1 rounded-lg text-sm flex items-center gap-3
                                            ${source.paths.some((p) => this.selectedPaths.has(p.path)) || this.selectedPaths.has(source.sourcePath)
                                            ? "bg-blue-100 text-blue-800"
                                            : "text-gray-600"}
                                            transition-colors cursor-pointer"
                                        @click=${(e: MouseEvent) => {
                                            const target = e.target as HTMLElement;
                                            if (!target.closest(".chevron-click-area")) {
                                                e.preventDefault();
                                                this.togglePath(source.sourcePath);
                                            }
                                        }}
                                    >
                                        <img
                                            src="/img/${getSourcePath(source.sourcePath)}.jpg"
                                            alt="${source.displayName}"
                                            class="w-8 h-8 rounded-md object-cover flex-shrink-0"
                                        />
                                        <div class="flex-1 min-w-0 flex justify-between items-center">
                                            <span class="font-medium truncate">${source.displayName}</span>
                                            <div class="flex items-center gap-2">
                                                <span
                                                    class="text-xs ${source.paths.every((p) => this.selectedPaths.has(p.path))
                                                        ? "text-blue-600"
                                                        : "text-gray-400"}"
                                                    >${source.paths.length
                                                        ? source.paths.reduce((sum, p) => sum + p.count, 0)
                                                        : this.totalDocuments}</span
                                                >
                                                ${showChevron
                                                    ? html`
                                                          <div class="chevron-click-area">
                                                              <i class="w-5 h-5 icon transition-transform duration-200 group-open:rotate-180"
                                                                  >${chevronDownIcon}</i
                                                              >
                                                          </div>
                                                      `
                                                    : ""}
                                            </div>
                                        </div>
                                    </summary>
                                    <div class="mt-1 space-y-1 ml-2">
                                        ${source.paths.map(
                                            (path) => html`
                                                <button
                                                    @click=${() => this.togglePath(path.path)}
                                                    class="w-full text-left px-2 py-1 rounded-full text-sm flex justify-between items-center gap-4
                                                ${this.selectedPaths.has(path.path) || this.selectedPaths.has(source.sourcePath)
                                                        ? "bg-blue-100 text-blue-800"
                                                        : "text-gray-600"}
                                                transition-colors"
                                                >
                                                    <span class="truncate">${path.path.replace(source.sourcePath, "").replace(/^-/, "")}</span>
                                                    <span
                                                        class="text-xs ${this.selectedPaths.has(path.path) ||
                                                        this.selectedPaths.has(source.sourcePath)
                                                            ? "text-blue-600"
                                                            : "text-gray-400"}"
                                                        >${path.count}</span
                                                    >
                                                </button>
                                            `
                                        )}
                                    </div>
                                </details>
                            `;
                        })}
                    </div>
                </div>
            </div>
        `;
    }
}
